import React, {useMemo, useState, useEffect} from 'react';
import styles from './CI.module.css';
import cx from 'classnames';
import {useOnboardingContext} from '../../../contexts/OnboardingContext';
import ColorPicker from '../../../components/ColorPicker';
import Select from '../../../components/Select';
import {SelectOption} from '../../../components/Select';
import DropInput from '../../../components/DropInput';
import {faTimes} from '@fortawesome/free-solid-svg-icons';
import IconButton from '../../../components/IconButton';
import {showToast} from '../../../services/ToastService';
import {ColorService} from '../../../services/ColorService';
import {Color} from '../../../types/Color';
import {FontService} from '../../../services/FontService';
import {Font} from '../../../types/Font';
import FontCard from './FontCard';

interface CIStepProps {
    isActive?: boolean;
}

const CIStep: React.FC<CIStepProps> = ({isActive = false}) => {
    const {
        formData,
        setPrimaryColor,
        setSecondaryColor,
        setPrimaryColorName,
        setSecondaryColorName,
        setFont,
        setStyle,
        setFontPreviewText,
        setSelectedFont,
        addLogo,
        removeLogo,
        updateLogo
    } = useOnboardingContext();
    const [colors, setColors] = useState<string[]>([]);
    const [colorObjects, setColorObjects] = useState<Color[]>([]);
    const [fonts, setFonts] = useState<Font[]>([]);
    const selectedFont = formData.selectedFont;

    useEffect(() => {
        const loadColors = async () => {
            try {
                const fetchedColors = await ColorService.getAllColors();
                const colorValues = fetchedColors.map(c => c.color).filter(color => /^#[0-9A-Fa-f]{6}$/.test(color));
                setColors(colorValues);
                setColorObjects(fetchedColors);
            } catch (error) {
                console.error('Failed to load colors:', error);
            }
        };
        loadColors();
    }, []);

    useEffect(() => {
        const loadFonts = async () => {
            try {
                const fetchedFonts = await FontService.getAllFonts();
                setFonts(fetchedFonts);

                if (formData.font && fetchedFonts.length > 0) {
                    const matchedFont = fetchedFonts.find(f => f.name === formData.font || f.name.toLowerCase() === formData.font.toLowerCase());
                    if (matchedFont) {
                        setSelectedFont(matchedFont);
                    }
                }
            } catch (error) {
                console.error('Failed to load fonts:', error);
            }
        };
        loadFonts();
    }, []);

    useEffect(() => {
        if (formData.font && fonts.length > 0) {
            const matchedFont = fonts.find(f => f.name === formData.font || f.name.toLowerCase() === formData.font.toLowerCase());
            if (matchedFont) {
                setSelectedFont(matchedFont);
            } else {
                setSelectedFont(null);
            }
        } else {
            setSelectedFont(null);
        }
    }, [formData.font, fonts, setSelectedFont]);

    const styleOptions: SelectOption[] = [
        {value: 'normal', label: 'Normal'},
        {value: 'curved', label: 'Curved'},
    ];

    const colorPalette: string[] | undefined = useMemo(() => {
        return colors.length > 0 ? colors : undefined;
    }, [colors]);

    const handleColorChange = (property: string, value: string) => {
        if (property === 'primaryColor') {
            setPrimaryColor(value);
            const name = value
                ? colorObjects.find(c => c.color.toUpperCase() === value.toUpperCase())?.name ?? ''
                : '';
            setPrimaryColorName(name);
        } else if (property === 'secondaryColor') {
            setSecondaryColor(value);
            const name = value
                ? colorObjects.find(c => c.color.toUpperCase() === value.toUpperCase())?.name ?? ''
                : '';
            setSecondaryColorName(name);
        }
    };

    const handleSelectChange = (property: string, value: string) => {
        if (property === 'font') {
            const matchedFont = fonts.find(f => f.name === value || f.name.toLowerCase() === value.toLowerCase());
            if (matchedFont) {
                setSelectedFont(matchedFont);
            } else {
                setSelectedFont(null);
            }
            setFont(value);
        } else if (property === 'style') {
            setStyle(value);
        }
    };

    const fontOptions: SelectOption[] = fonts.map(font => ({
        value: font.name,
        label: font.name,
    }));

    const NON_IMAGE_EXTENSIONS = ['pdf', 'eps', 'ai'];

    const handleLogoFileChange = (logoId: string, file: File) => {
        const maxSizeInBytes = 15 * 1024 * 1024;
        if (file.size > maxSizeInBytes) {
            const fileSizeInMB = (file.size / (1024 * 1024)).toFixed(2);
            showToast(`Die Datei ist zu groß (${fileSizeInMB} MB). Maximale Dateigröße: 15 MB`, 'error');
            return;
        }

        const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
        const isNonImage = NON_IMAGE_EXTENSIONS.includes(ext);

        if (isNonImage) {
            updateLogo(logoId, {
                file,
                preview: '',
                fileExtension: ext,
            });
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            updateLogo(logoId, {
                file,
                preview: reader.result as string,
                fileExtension: '',
            });
        };
        reader.readAsDataURL(file);
    };

    const handleLogoLabelChange = (logoId: string, label: string) => {
        updateLogo(logoId, {label});
    };

    return (
        <div className={cx(styles['ci-step-container'])} hidden={!isActive}>
            <h2 className={styles['step-title']}>Firmenfarben, Schriftart & Logo(s)</h2>
            <div className={styles['step-content']}>
                <div className={styles['ci-columns']}>
                    <div className={styles['ci-column-left']}>


                        <div className={styles['ci-section-font']}>
                            <h3 className={styles['section-title']}>Schriftart</h3>
                            <Select
                                options={fontOptions}
                                onSelect={handleSelectChange}
                                property="font"
                                value={formData.font}
                                placeholder="Schriftart auswählen"
                                searchable={false}
                            />
                        </div>

                        <div className={styles['ci-section']}>
                            <Select
                                options={styleOptions}
                                onSelect={handleSelectChange}
                                property="style"
                                value={formData.style}
                                placeholder="Style auswählen"
                                searchable={false}
                            />
                        </div>

                        {selectedFont && (
                            <div className={styles['ci-section']}>
                                <h4 className={styles['preview-title']}>Vorschau</h4>
                                <div className={styles['font-preview-wrapper']}>
                                    <FontCard
                                        key={selectedFont.id}
                                        font={selectedFont}
                                        onClick={() => {
                                        }}
                                        chosen={true}
                                        style={formData.style as 'normal' | 'curved' | undefined}
                                        previewText={formData.fontPreviewText || 'workwear'}
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Text eingeben zum Testen der Schriftart"
                                    value={formData.fontPreviewText || ''}
                                    onChange={(e) => setFontPreviewText(e.target.value)}
                                    className={styles['font-preview-input']}
                                    aria-label="Vorschau-Text für Schriftart"
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles['ci-column-center']}>
                        <div className={styles['ci-section']}>
                            <h3 className={styles['section-title']}>Firmenfarben</h3>
                            <div className={styles['color-pickers-row']}>
                                <div className={styles['color-picker-wrapper']}>
                                    <ColorPicker
                                        property="primaryColor"
                                        value={formData.primaryColor}
                                        onChange={handleColorChange}
                                        colors={colorPalette}
                                        colorObjects={colorObjects}
                                    >
                                        Hauptfarbe
                                    </ColorPicker>
                                </div>
                                <div className={styles['color-picker-wrapper']}>
                                    <ColorPicker
                                        property="secondaryColor"
                                        value={formData.secondaryColor}
                                        onChange={handleColorChange}
                                        colors={colorPalette}
                                        colorObjects={colorObjects}
                                    >
                                        Sekundärfarbe
                                    </ColorPicker>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={styles['ci-column-right']}>
                        <div className={styles['ci-section']}>
                            <h3 className={styles['section-title']}>Logo(s)</h3>
                            <div className={styles['logos-info-wrapper']}>
                                <div className={styles['logos-info-title']}>Dateien</div>
                                <div className={styles['logos-list']}>
                                    {formData.logos.map((logo) => {
                                        const logoExt = logo.fileExtension ?? logo.file?.name.split('.').pop()?.toLowerCase() ?? '';
                                        const isPdfEpsAi = ['pdf', 'eps', 'ai'].includes(logoExt);
                                        const dropFileTypeLabel = logo.file && isPdfEpsAi ? logoExt.toUpperCase() : undefined;
                                        return (
                                        <div key={logo.id} className={styles['logo-item']}>
                                            <div className={styles['logo-input-wrapper']}>
                                                <div className={styles['logo-icon-wrapper']}>
                                                    {isPdfEpsAi ? (
                                                        <span className={styles['logo-file-badge']}>
                                                            {logoExt.toUpperCase()}
                                                        </span>
                                                    ) : logo.preview ? (
                                                        <img src={logo.preview} alt="Logo preview"
                                                             className={styles['logo-preview']}/>
                                                    ) : (
                                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                            <path
                                                                d="M2.5 5.83333C2.5 4.91286 3.24619 4.16667 4.16667 4.16667H7.5L9.16667 6.66667H15.8333C16.7538 6.66667 17.5 7.41286 17.5 8.33333V15C17.5 15.9205 16.7538 16.6667 15.8333 16.6667H4.16667C3.24619 16.6667 2.5 15.9205 2.5 15V5.83333Z"
                                                                stroke="#666" strokeWidth="1.5" strokeLinecap="round"
                                                                strokeLinejoin="round"/>
                                                            <circle cx="7.5" cy="11.6667" r="1.66667" stroke="#666"
                                                                    strokeWidth="1.5"/>
                                                        </svg>
                                                    )}
                                                    <DropInput
                                                        accept="image/*,.pdf,application/pdf,.eps,.ai,application/postscript,application/illustrator"
                                                        previewSrc={logo.preview}
                                                        fileTypeLabel={dropFileTypeLabel}
                                                        onFileChange={(file) => handleLogoFileChange(logo.id, file)}
                                                        className={styles['logo-drop-input']}
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    placeholder="Logo beschreibung"
                                                    value={logo.label}
                                                    onChange={(e) => handleLogoLabelChange(logo.id, e.target.value)}
                                                    className={styles['logo-text-input']}
                                                />
                                                <IconButton
                                                    icon={faTimes}
                                                    color="#666"
                                                    title="Logo entfernen"
                                                    onClick={() => removeLogo(logo.id)}
                                                    classes={styles['logo-remove-button']}
                                                />
                                            </div>
                                        </div>
                                        );
                                    })}
                                    <button
                                        className={styles['add-logo-button']}
                                        onClick={addLogo}
                                        type="button"
                                    >
                                        <span className={styles['add-logo-icon']}>+</span>
                                    </button>
                                </div>
                                <div className={styles['step-infobox']}>
                                    <p className={styles['step-infobox-text']}>
                                        Nach Möglichkeit Vektordateien, wie pdf, svg, eps hochladen hochladen
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CIStep;

